/**
 * <ExtraHoursParametersStep> — les quatre questions d'ouverture d'un cours ponctuel (organism).
 *
 * Combien d'heures ? Quel format ? Pour quand ? Quelle matière ? Les mots sont ceux du legacy
 * (`parentExtraHours/ParametersStep.js`), au mot près. « Pour quand ? » disparaît en format
 * individuel : le parent y donne des dates exactes à l'étape suivante, l'échéance n'y sert plus.
 *
 * Contrôlé : le brouillon arrive en prop et repart par `onChange`. Une seule chose vit ici, et
 * c'est de l'interface : le repli « Voir d'autres matières ». Il s'ouvre d'emblée quand la
 * matière choisie n'est pas dans les objectifs de l'élève — sinon un aller-retour vers l'étape
 * suivante cacherait le choix que le parent vient de faire.
 *
 * Les tarifs, les échéances et les matières sont des catalogues injectés : aucune liste, aucun
 * prix, aucune date n'est écrite dans le design system.
 */
import React, { useState } from "react"

import { TextButton } from "../../atoms/Buttons/TextButton"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { ChoiceChip } from "../../atoms/ChoiceChip"
import { IconBubble } from "../../atoms/icons/IconBubble"
import { SubjectBadge } from "../../atoms/SubjectBadge"
import { Text } from "../../atoms/typography/Text"
import { DatePicker } from "../../molecules/DatePicker"
import { HorizontalSelectInput } from "../../molecules/HorizontalSelectInput"
import { OptionCard } from "../../molecules/OptionCard"
import {
    type UIExtraHoursDeadlineOption,
    type UIExtraHoursDraft,
    type UIExtraHoursFormatOption,
    type UISubject,
} from "../../../ports/primary/ui/types"

export type ExtraHoursParametersStepProps = {
    draft: UIExtraHoursDraft
    onChange: (next: UIExtraHoursDraft) => void

    /** Les nombres d'heures proposés. */
    hourOptions: number[]
    formatOptions: UIExtraHoursFormatOption[]
    deadlineOptions: UIExtraHoursDeadlineOption[]

    /** Les matières des objectifs de l'élève — celles qu'on propose d'abord. */
    subjects: UISubject[]
    /** Les autres matières de sa classe, derrière le repli. */
    otherSubjects?: UISubject[]

    /** Bornes du calendrier de l'échéance précise. */
    minDeadlineDate?: Date
    maxDeadlineDate?: Date

    disabled?: boolean
    testID?: string
}

export function ExtraHoursParametersStep(props: ExtraHoursParametersStepProps) {
    const {
        draft,
        onChange,
        hourOptions,
        formatOptions,
        deadlineOptions,
        subjects,
        otherSubjects = [],
        minDeadlineDate,
        maxDeadlineDate,
        disabled = false,
        testID,
    } = props

    const patch = (partial: Partial<UIExtraHoursDraft>) => onChange({ ...draft, ...partial })

    const isObjectiveSubject = subjects.some((s) => String(s.id) === String(draft.subjectId))
    const [showAll, setShowAll] = useState(() => draft.subjectId != null && subjects.length > 0 && !isObjectiveSubject)

    // Sans objectif, il n'y a rien à mettre en avant : tout le catalogue de la classe s'affiche.
    const visibleSubjects = showAll || subjects.length === 0 ? [...subjects, ...otherSubjects] : subjects
    const canExpand = otherSubjects.length > 0 && subjects.length > 0

    const isIndividual = draft.format === "individual"
    const customDeadlineChosen = deadlineOptions.some((o) => o.custom === true && o.value === draft.deadlineType)

    return (
        <Stack gap="6" testID={testID}>
            <HorizontalSelectInput
                label="Combien d'heures ?"
                items={hourOptions}
                getItemId={(h) => h}
                renderItem={(h) => (
                    <ChoiceChip
                        variant="pill"
                        label={`${h} heure${h > 1 ? "s" : ""}`}
                        selected={draft.hours === h}
                        onPress={() => patch({ hours: h })}
                        disabled={disabled}
                    />
                )}
            />

            <Stack gap="3">
                {/* Même libellé de champ que `<DatePicker>` et `<HorizontalSelectInput>` : les
                    deux options se posent l'une sous l'autre, pas en rangée, donc le libellé ne
                    peut pas venir de la molécule. */}
                <Text size="text-base" weight="bold" color="primary">
                    Quel format ?
                </Text>
                <Stack gap="2">
                    {formatOptions.map((option) => (
                        <OptionCard
                            key={option.value}
                            label={option.label}
                            description={option.description}
                            badge={option.priceLabel}
                            badgeVariant="primary"
                            leading={<IconBubble iconName={option.icon} size={36} />}
                            selected={draft.format === option.value}
                            disabled={disabled}
                            onPress={() => patch({ format: option.value })}
                        />
                    ))}
                </Stack>
            </Stack>

            {!isIndividual && (
                <Stack gap="3">
                    <HorizontalSelectInput
                        label="Pour quand ?"
                        items={deadlineOptions}
                        getItemId={(o) => o.value}
                        renderItem={(option) => (
                            <ChoiceChip
                                variant="pill"
                                label={option.label}
                                selected={draft.deadlineType === option.value}
                                onPress={() => patch({ deadlineType: option.value })}
                                disabled={disabled}
                            />
                        )}
                    />
                    {customDeadlineChosen && (
                        <DatePicker
                            value={draft.customDeadline ?? ""}
                            onChange={(customDeadline) => patch({ customDeadline })}
                            placeholder="Choisir une date"
                            modalLabel="Échéance souhaitée"
                            minDate={minDeadlineDate}
                            maxDate={maxDeadlineDate}
                            disabled={disabled}
                        />
                    )}
                </Stack>
            )}

            <Stack gap="2">
                <HorizontalSelectInput
                    label="Quelle matière ?"
                    items={visibleSubjects}
                    getItemId={(s) => s.id}
                    renderItem={(subject) => (
                        <SubjectBadge
                            subject={subject}
                            size="md"
                            showLabel
                            selected={String(subject.id) === String(draft.subjectId)}
                            onSelect={() => patch({ subjectId: subject.id })}
                            disabled={disabled}
                        />
                    )}
                />
                {canExpand && (
                    // Une `<Stack>` étire ses enfants : sans la rangée, le lien occuperait toute la
                    // largeur et son libellé se centrerait au milieu de la feuille.
                    <Row>
                        <TextButton
                            label={showAll ? "Masquer les autres matières" : "Voir d'autres matières"}
                            size="sm"
                            onPress={() => setShowAll((prev) => !prev)}
                            disabled={disabled}
                        />
                    </Row>
                )}
            </Stack>
        </Stack>
    )
}
